/* eslint-disable @typescript-eslint/naming-convention */
import {ChangeEventHandler, FC, useCallback, useMemo, useState} from 'react';
import {Element} from 'react-scroll';
import classNames from 'classnames';
import {Typography} from 'components/Typography';
import {TextArea} from 'components/TextArea';
import {Button} from 'components/Button';
import {MobileModal} from 'modules/modals/components/MobileModal';
import {MODALS} from 'modules/modals/constants';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {showModal} from 'modules/modals/thunks';
import {PRODUCT_REVIEW_HASH_PREFIX} from 'modules/products/constants';
import {TPropsWithClassName} from 'core/types';
import {IReview, IVoteReviewRequest} from 'modules/product-card/models/Review';
import style from './style.module.scss';
import {useCreateCommentMutation, useVoteReviewMutation} from 'modules/product-card/services';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectProductData} from 'modules/product-card/selectors';
import {showAuthDialog} from 'modules/auth/thunks';
import {ReviewModerationCaption} from 'modules/products/components/ReviewModerationCaption';
import {CollapseTextInline} from 'components/CollapseTextInline';
import {CollapseAnimated} from 'components/CollapseAnimated';
import {useIsLoggedIn} from 'modules/user/hooks/useIsLoggedIn';
import {ProductReviewCardHeader} from 'modules/products/components/ProductReviewCardHeader';
import {ProductReviewCardActions} from 'modules/products/components/ProductReviewCardActions';
import {ProductReviewCommentsList} from 'modules/products/components/ProductReviewCommentsList';
import {UNKOWN_NAME} from 'modules/products/constants';

interface IProps {
    review: IReview;
    isSimpleView?: boolean;
    withScroll?: boolean;
    slidesBaseKey?: number;
    collapsed?: boolean;
}

const MAX_TEXT_LENGTH = 333;

// eslint-disable-next-line max-lines-per-function,complexity
export const ProductReviewCard: FC<TPropsWithClassName<IProps>> = ({
    review,
    isSimpleView = false,
    withScroll = true,
    collapsed = true,
    slidesBaseKey,
    className,
}) => {
    const isLoggedIn = useIsLoggedIn();
    const productData = useAppSelector(selectProductData);

    const {productId, offerId} = productData || {};

    const {rating, author, likesCount, dislikesCount, createdAt, comments, id} = review;

    const [createComment, commentResult] = useCreateCommentMutation();
    const [vote] = useVoteReviewMutation();

    const [likes, setLikes] = useState<number>(likesCount);
    const [dislikes, setDislikes] = useState<number>(dislikesCount);

    const dispatch = useAppDispatch();

    const [isCommentOpen, setIsCommentOpen] = useState(false);
    const [commentValue, setCommentValue] = useState('');
    const [isLike, setIsLike] = useState(review.isLikedByCurrentUser);
    const [isDislike, setIsDislike] = useState(review.isDislikedByCurrentUser);

    const handleCommentToggle = () => {
        if (!checkIsLoggedIn()) {
            return;
        }

        setIsCommentOpen(!isCommentOpen);
    };

    const handleCommentChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => setCommentValue(event.target.value);

    const handleCancel = useCallback(() => {
        setIsCommentOpen(false);
        setCommentValue('');
    }, []);

    const handleSend = useCallback(async () => {
        if (productId && offerId) {
            await createComment({comment: commentValue, offerId, productId, reviewId: id});
            dispatch(showModal(MODALS.PRODUCT_COMMENT_MODERATION.name));
            handleCancel();
        }
    }, [commentValue, createComment, dispatch, handleCancel, id, offerId, productId]);

    const checkIsLoggedIn = () => {
        if (!isLoggedIn) {
            dispatch(showAuthDialog);
        }

        return isLoggedIn;
    };

    const handleVote = async (args: IVoteReviewRequest) => {
        const {likes: lk, dislikes: ds} = await vote(args).unwrap();
        setLikes(lk);
        setDislikes(ds);
    };

    const onLike = () => {
        if (!checkIsLoggedIn()) {
            return;
        }

        const ratingValue = isLike ? 0 : 1;
        handleVote({rating: ratingValue, reviewId: id})
            .then(() => {
                setIsLike(!isLike);
                setIsDislike(false);
            })
            .catch((e) => {
                console.error(e);
            });
    };

    const onDislike = () => {
        if (!checkIsLoggedIn()) {
            return;
        }

        const ratingValue = isDislike ? 0 : -1;
        handleVote({rating: ratingValue, reviewId: id})
            .then(() => {
                setIsDislike(!isDislike);
                setIsLike(false);
            })
            .catch((e) => {
                console.error(e);
            });
    };

    const commentBlock = useMemo(() => {
        return (
            <div>
                <div className={style.textareaWrapper}>
                    <Typography
                        className={style.mobileSubtitle}
                        color={'gray60'}
                        element={'div'}
                        variant={'label-16-r'}
                    >
                        Комментарий
                    </Typography>
                    <TextArea
                        captionText={<ReviewModerationCaption entityLabel={'комментарий'} />}
                        placeholder={'Ваш комментарий'}
                        disabled={commentResult.isLoading}
                        onChange={handleCommentChange}
                    />
                </div>
                <div className={style.commentActions}>
                    <Button
                        className={style.cancel}
                        disabled={commentResult.isLoading}
                        onClick={handleCancel}
                        size={'huge'}
                        theme={'secondary'}
                    >
                        Отмена
                    </Button>
                    <Button
                        disabled={!Boolean(commentValue)}
                        isLoading={commentResult.isLoading}
                        onClick={handleSend}
                        size={'huge'}
                    >
                        Отправить
                    </Button>
                </div>
            </div>
        );
    }, [commentValue, handleCancel, handleSend, commentResult.isLoading]);

    const authorName = useMemo(() => {
        if (author?.fullName) {
            return author.fullName;
        }
        return UNKOWN_NAME;
    }, [author]);

    // const resultPrice = useProductResultPrice(productData);

    // const normalizedPrice = resultPrice
    //     ?.replace(/[^\d.,]/gu, '')
    //     .replace(/\s/gu, '')
    //     .replace(',', '.')
    //     .trim();

    // const reviewStructuredData = useMemo(
    //     () => ({
    //         '@context': 'https://schema.org',
    //         '@type': 'Review',
    //         datePublished: createdAt,
    //         reviewBody: review.comment,
    //         itemReviewed: catalogRusName
    //             ? {
    //                   '@type': 'Product',
    //                   name: catalogRusName,
    //                   ...(media?.imagePreview ? {image: getMediaSrc(media.imagePreview)} : {}),
    //                   offers: normalizedPrice
    //                       ? {
    //                             '@type': 'Offer',
    //                             price: normalizedPrice,
    //                             priceCurrency: 'RUB',
    //                             availability:
    //                                 canBuy || canReserve ? 'https://schema.org/InStock' : 'https://schema.org/PreOrder',
    //                         }
    //                       : undefined,
    //               }
    //             : undefined,
    //         author: fullAuthorName
    //             ? {
    //                   '@type': 'Person',
    //                   name: fullAuthorName,
    //               }
    //             : undefined,
    //         reviewRating: rating
    //             ? {
    //                   '@type': 'Rating',
    //                   ratingValue: Number(rating),
    //               }
    //             : undefined,
    //     }),
    //     [
    //         canBuy,
    //         canReserve,
    //         catalogRusName,
    //         createdAt,
    //         fullAuthorName,
    //         media?.imagePreview,
    //         normalizedPrice,
    //         rating,
    //         review.comment,
    //     ],
    // );

    return (
        <>
            {withScroll && <Element name={`${PRODUCT_REVIEW_HASH_PREFIX}${id}`} />}
            <div
                className={classNames(style.card, className, {
                    [style.fixedWidth]: isSimpleView && 114 >= (review.comment?.length ?? 0),
                })}
                id={review.id.toString()}
            >
                {/* <script*/}
                {/*    type="application/ld+json"*/}
                {/*    // eslint-disable-next-line react/no-danger*/}
                {/*    dangerouslySetInnerHTML={{__html: JSON.stringify(reviewStructuredData)}}*/}
                {/* />*/}
                <ProductReviewCardHeader author={author} fullName={authorName} rate={Number(rating)} review={review} />

                {!isSimpleView && (
                    <div className={style.tags}>
                        {Array.isArray(review.tasteTags) &&
                            review.tasteTags?.map((tag) => (
                                <Typography className={style.tag} element={'div'} key={tag} variant={'label-14-m'}>
                                    {tag}
                                </Typography>
                            ))}
                    </div>
                )}
                <div className={style.contentWrapper}>
                    {collapsed ? (
                        <CollapseAnimated>
                            <Typography className={classNames(style.text, 'review-card-text')}>
                                <CollapseTextInline
                                    isTextClickable={true}
                                    buttonTextVariant={'p'}
                                    collapsedButtonText={'eщe'}
                                    key={slidesBaseKey}
                                    text={review.comment}
                                    maxTextLength={MAX_TEXT_LENGTH}
                                />
                            </Typography>
                        </CollapseAnimated>
                    ) : (
                        <Typography className={classNames(style.text, 'review-card-text')}>{review.comment}</Typography>
                    )}
                </div>
                <ProductReviewCardActions
                    date={createdAt}
                    dislikes={dislikes}
                    isCommentText={!isSimpleView}
                    isDislike={isDislike}
                    isLike={isLike}
                    likes={likes}
                    onCommentToggle={!isSimpleView ? handleCommentToggle : undefined}
                    onDislikeClick={onDislike}
                    onLikeClick={onLike}
                />
                {!isSimpleView && (
                    <>
                        {isCommentOpen && (
                            <>
                                <div className={style.desktopComment}>{commentBlock}</div>
                                <MobileModal
                                    className={style.commentModal}
                                    classNames={{title: style.modalTitle}}
                                    onClose={handleCommentToggle}
                                    title={'Комментарий'}
                                >
                                    {commentBlock}
                                </MobileModal>
                            </>
                        )}
                        {0 < comments.length && <ProductReviewCommentsList comments={comments} />}
                    </>
                )}
            </div>
        </>
    );
};
